/**
 * Rechnungen Component
 * Displays invoices, positions, and installment schedules for students.
 * Supports filtering (hide paid, hide planned), summary stats split by planned/not-planned,
 * accordion expansion via Angular CDK, and opening invoice PDFs.
 */

import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  computed,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { TranslatePipe, I18nStore } from '../../core/features/translations';
import { AuthService } from '../../services/auth.service';
import { ContactService } from '../../core/services/contact.service';
import { StudentInvoiceService } from '../../core/services/student-invoice.service';
import {
  StudentInvoice,
  StudentInvoiceDetailResponse,
  StudentInvoiceInstallment,
  InvoiceStatusId,
  StudentInstallmentStatus,
} from '../../core/models';
import { switchMap, catchError, of, forkJoin } from 'rxjs';

@Component({
  selector: 'app-rechnungen',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CdkAccordionModule,
    TranslatePipe,
    DatePipe,
  ],
  template: `
    <div class="p-4 md:p-8 max-w-[1300px] mx-auto animate-fade-in font-sans">
      <!-- SUMMARY BAR SPLIT IN PLANNED & NOT PLANNED -->
      @if (!loading() && invoices().length > 0) {
        <div class="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- CARD 1: NOT PLANNED (ACTIVE / SENT / PAID / DUE) -->
          <div class="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-5 flex flex-col justify-between transition-all hover:shadow-md">
            <div class="flex items-center justify-between gap-2 mb-3">
              <div class="flex items-center gap-2">
                <span class="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <span class="material-symbols-outlined text-lg">receipt_long</span>
                </span>
                <span class="text-xs font-black uppercase tracking-wider text-gray-700">
                  {{ 'portal.invoices.summary.activeInvoices' | translate }}
                </span>
              </div>
              <span class="px-2.5 py-0.5 rounded-full text-xs font-black bg-blue-50 text-blue-700 border border-blue-100">
                {{ notPlannedCount() }} {{ 'portal.invoices.invoicesUnit' | translate }}
              </span>
            </div>

            <div class="flex items-baseline justify-between pt-2 border-t border-gray-100">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wider">
                {{ 'finance.open_amount' | translate }}
              </span>
              <span class="text-xl font-black tabular-nums"
                    [class.text-emerald-600]="notPlannedOpenAmount() <= 0"
                    [class.text-amber-600]="notPlannedOpenAmount() > 0">
                {{ formatCurrency(notPlannedOpenAmount()) }}
              </span>
            </div>
          </div>

          <!-- CARD 2: PLANNED INVOICES (GEPLANT - STATUS 650) -->
          <div class="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-5 flex flex-col justify-between transition-all hover:shadow-md">
            <div class="flex items-center justify-between gap-2 mb-3">
              <div class="flex items-center gap-2">
                <span class="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                  <span class="material-symbols-outlined text-lg">event_upcoming</span>
                </span>
                <span class="text-xs font-black uppercase tracking-wider text-gray-700">
                  {{ 'portal.invoices.summary.plannedInvoices' | translate }}
                </span>
              </div>
              <span class="px-2.5 py-0.5 rounded-full text-xs font-black bg-amber-50 text-amber-800 border border-amber-200">
                {{ plannedCount() }} {{ 'portal.invoices.invoicesUnit' | translate }}
              </span>
            </div>

            <div class="flex items-baseline justify-between pt-2 border-t border-gray-100">
              <span class="text-xs font-bold text-gray-500 uppercase tracking-wider">
                {{ 'portal.invoices.summary.plannedAmount' | translate }}
              </span>
              <span class="text-xl font-black text-gray-900 tabular-nums">
                {{ formatCurrency(plannedOpenAmount()) }}
              </span>
            </div>
          </div>
        </div>
      }

      <!-- ATTRIBUTE SECTION: PAYMENT IN INSTALLMENTS PER SCHOOL -->
      @if (!loading() && schoolInstallmentStatuses().length > 0) {
        <div class="mb-6 bg-white rounded-2xl border border-gray-200/80 shadow-sm p-5 transition-all">
          <div class="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
            <span class="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <span class="material-symbols-outlined text-lg">credit_card</span>
            </span>
            <div>
              <h2 class="text-sm font-black text-gray-900 m-0">
                {{ 'sis.attributes.payment_in_installments_label' | translate }}
              </h2>
              <p class="text-xs text-gray-500 m-0">
                {{ 'portal.invoices.installments.sectionDesc' | translate }}
              </p>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            @for (school of schoolInstallmentStatuses(); track school.school_id) {
              <div class="bg-gray-50/80 rounded-xl p-4 border border-gray-200/70 flex flex-col justify-between gap-3">
                <div class="flex items-start justify-between gap-3">
                  <div class="flex flex-col gap-1">
                    <div class="flex items-center gap-2">
                      <span class="px-2 py-0.5 rounded text-[11px] font-black uppercase tracking-wider bg-white text-gray-700 border border-gray-200 shadow-2xs">
                        {{ school.school_shortname || school.school_name }}
                      </span>
                      <span class="text-xs font-bold text-gray-900">
                        {{ school.school_name }}
                      </span>
                    </div>

                    <!-- Current Mode Badge -->
                    <div class="flex items-center gap-2 mt-1">
                      <span class="text-xs text-gray-500 font-medium">
                        {{ 'portal.invoices.installments.currentMode' | translate }}:
                      </span>
                      @if (school.has_installments_enabled) {
                        <span class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200 flex items-center gap-1.5">
                          <span class="w-1.5 h-1.5 rounded-full bg-indigo-600"></span>
                          {{ 'portal.invoices.installments.sixInstallments' | translate }}
                        </span>
                      } @else {
                        <span class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-700 border border-gray-200 flex items-center gap-1.5">
                          <span class="w-1.5 h-1.5 rounded-full bg-gray-500"></span>
                          {{ 'portal.invoices.installments.oneInstallment' | translate }}
                        </span>
                      }
                    </div>

                    <!-- School Surcharge Rule / Info -->
                    <div class="text-xs text-gray-600 mt-1 flex items-center gap-1.5">
                      <span class="material-symbols-outlined text-sm text-gray-400">info</span>
                      <span>{{ getSurchargeDescription(school) }}</span>
                    </div>

                    @if (school.planned_invoices_count > 0) {
                      <div class="text-[11px] text-amber-700 mt-0.5 font-medium">
                        {{ school.planned_invoices_count }} {{ 'portal.invoices.installments.plannedAffected' | translate }}
                      </div>
                    }
                  </div>
                </div>

                <!-- Action Button -->
                <div class="pt-3 border-t border-gray-200/60 flex items-center justify-between gap-6">
                  <span class="text-xs text-gray-500 font-medium">
                    {{ school.has_installments_enabled ? ('portal.invoices.installments.switchToOne' | translate) : ('portal.invoices.installments.switchToSix' | translate) }}
                  </span>
                  <button
                    type="button"
                    [disabled]="recalculatingSchoolId() === school.school_id"
                    (click)="openConfirmInstallmentModal(school)"
                    class="px-3.5 py-1.5 rounded-xl font-bold text-xs transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    [ngClass]="school.has_installments_enabled
                      ? 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-300'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-100'"
                  >
                    @if (recalculatingSchoolId() === school.school_id) {
                      <div class="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      <span>{{ 'portal.invoices.installments.updating' | translate }}</span>
                    } @else {
                      <span class="material-symbols-outlined text-sm">swap_horiz</span>
                      <span>
                        {{ school.has_installments_enabled ? ('portal.invoices.installments.btnSwitchToOne' | translate) : ('portal.invoices.installments.btnSwitchToSix' | translate) }}
                      </span>
                    }
                  </button>
                </div>
              </div>
            }
          </div>
        </div>
      }

      <!-- FILTER / TOOLBAR -->
      <div class="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        <div class="flex items-center gap-6 flex-wrap">
          <!-- Hide Paid Checkbox -->
          <label class="flex items-center gap-2.5 cursor-pointer select-none text-sm font-semibold text-gray-700">
            <input
              type="checkbox"
              [checked]="hidePaidInvoices()"
              (change)="toggleHidePaid()"
              class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
            />
            <span>{{ 'portal.invoices.filter.hidePaid' | translate }}</span>
            <span class="text-xs text-gray-400 font-normal">({{ paidInvoicesCount() }})</span>
          </label>

          <!-- Hide Planned Checkbox -->
          <label class="flex items-center gap-2.5 cursor-pointer select-none text-sm font-semibold text-gray-700">
            <input
              type="checkbox"
              [checked]="hidePlannedInvoices()"
              (change)="toggleHidePlanned()"
              class="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
            />
            <span>{{ 'portal.invoices.filter.hidePlanned' | translate }}</span>
            <span class="text-xs text-gray-400 font-normal">({{ plannedCount() }})</span>
          </label>
        </div>
      </div>

      <!-- LOADING STATE -->
      @if (loading()) {
        <div class="flex flex-col items-center justify-center p-20 text-center gap-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <div class="w-14 h-14 border-4 border-gray-100 border-t-blue-600 rounded-full animate-spin"></div>
          <p class="text-gray-500 text-base font-semibold m-0">
            {{ 'portal.invoices.loading' | translate }}
          </p>
        </div>
      }

      <!-- ERROR STATE -->
      @if (error() && !loading()) {
        <div class="flex flex-col items-center justify-center p-12 text-center gap-4 bg-red-50 rounded-2xl border border-red-200 shadow-sm">
          <span class="material-symbols-outlined text-red-500 text-5xl">warning</span>
          <p class="text-red-900 text-base font-bold m-0 max-w-lg">{{ error() }}</p>
          <button
            type="button"
            (click)="loadInvoicesAndData()"
            class="px-5 py-2.5 bg-red-600 text-white rounded-xl font-bold text-sm shadow hover:bg-red-700 transition-all focus:outline-none"
          >
            <span class="material-symbols-outlined text-sm mr-1.5 align-middle">refresh</span>
            {{ 'portal.degrees.retry' | translate }}
          </button>
        </div>
      }

      <!-- EMPTY STATE -->
      @if (!loading() && !error() && displayedInvoices().length === 0) {
        <div class="bg-white rounded-2xl shadow-sm p-16 border border-gray-100 text-center flex flex-col items-center">
          <div class="w-20 h-20 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mb-4">
            <span class="material-symbols-outlined text-4xl">receipt</span>
          </div>
          <h2 class="text-2xl font-bold text-gray-800 m-0 mb-2">
            {{ 'portal.invoices.empty.title' | translate }}
          </h2>
          <p class="text-gray-500 text-sm max-w-md mx-auto leading-relaxed m-0">
            {{ (hidePaidInvoices() || hidePlannedInvoices() ? 'portal.invoices.empty.hiddenPaidMessage' : 'portal.invoices.empty.message') | translate }}
          </p>
        </div>
      }

      <!-- INVOICES ACCORDION LIST (ANGULAR SDK CDK) -->
      @if (!loading() && !error() && displayedInvoices().length > 0) {
        <cdk-accordion class="flex flex-col gap-3">
          @for (inv of displayedInvoices(); track inv.invoice_id) {
            <cdk-accordion-item
              #accordionItem="cdkAccordionItem"
              class="block rounded-2xl border border-gray-200/80 bg-white shadow-sm overflow-hidden transition-all duration-200 hover:border-blue-300"
              [class.ring-2]="accordionItem.expanded"
              [class.ring-blue-500/20]="accordionItem.expanded"
              [class.border-blue-500]="accordionItem.expanded"
            >
              <!-- ACCORDION HEADER (ROW) -->
              <div
                class="p-4 md:p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4 cursor-pointer select-none transition-colors"
                [class.bg-blue-50/20]="accordionItem.expanded"
                (click)="onToggleAccordion(accordionItem, inv)"
              >
                <!-- Left: Identity, Degree, Dates -->
                <div class="flex items-center gap-4 min-w-0">
                  <!-- Expand Icon -->
                  <div class="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-500 transition-transform duration-200"
                       [class.rotate-90]="accordionItem.expanded">
                    <span class="material-symbols-outlined text-xl">chevron_right</span>
                  </div>

                  <div class="min-w-0">
                    <div class="flex items-center gap-2 mb-1 flex-wrap">
                      <span class="text-sm font-black text-gray-900 tracking-tight">
                        #{{ inv.invoice_id }}
                      </span>
                      @if (inv.degree_code) {
                        <span class="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-[11px] font-extrabold uppercase border border-indigo-100">
                          {{ inv.degree_code }}
                        </span>
                      }
                      <span class="px-2 py-0.5 rounded-md text-[11px] font-bold uppercase border"
                            [class]="getStatusBadgeClass(inv)">
                        {{ getStatusLabel(inv) }}
                      </span>
                    </div>

                    <div class="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                      <span class="flex items-center gap-1">
                        <span class="material-symbols-outlined text-xs text-gray-400">event</span>
                        {{ 'finance.invoice_date' | translate }}: {{ inv.invoice_date | date:'dd.MM.yyyy' }}
                      </span>
                      <span class="flex items-center gap-1">
                        <span class="material-symbols-outlined text-xs text-gray-400">schedule</span>
                        {{ 'finance.due_date' | translate }}: {{ inv.due_date | date:'dd.MM.yyyy' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Right: Amounts and Actions -->
                <div class="flex items-center justify-between lg:justify-end gap-6 shrink-0 pt-2 lg:pt-0 border-t lg:border-t-0 border-gray-100">
                  <div class="flex items-center gap-6">
                    <!-- Total Amount -->
                    <div class="flex flex-col text-right">
                      <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        {{ 'finance.amount_gross' | translate }}
                      </span>
                      <span class="text-sm font-black text-gray-900">
                        {{ formatCurrency(inv.amount_gross) }}
                      </span>
                    </div>

                    <!-- Open Amount -->
                    <div class="flex flex-col text-right">
                      <span class="text-[10px] font-bold uppercase tracking-wider"
                            [class.text-emerald-600]="inv.open_amount <= 0"
                            [class.text-amber-600]="inv.open_amount > 0">
                        {{ 'finance.open_amount' | translate }}
                      </span>
                      <span class="text-sm font-black"
                            [class.text-emerald-600]="inv.open_amount <= 0"
                            [class.text-amber-600]="inv.open_amount > 0">
                        {{ formatCurrency(inv.open_amount) }}
                      </span>
                    </div>
                  </div>

                  <!-- PDF Download / View Button -->
                  <div class="flex items-center gap-1" (click)="$event.stopPropagation()">
                    <button
                      type="button"
                      [disabled]="!isPdfAvailable(inv) || openingPdf()[inv.invoice_id]"
                      (click)="fetchAndOpenPdf(inv)"
                      class="w-9 h-9 rounded-xl flex items-center justify-center transition-all shadow-sm focus:outline-none"
                      [class.bg-blue-50]="isPdfAvailable(inv) && !openingPdf()[inv.invoice_id]"
                      [class.text-blue-600]="isPdfAvailable(inv) && !openingPdf()[inv.invoice_id]"
                      [class.hover:bg-blue-600]="isPdfAvailable(inv) && !openingPdf()[inv.invoice_id]"
                      [class.hover:text-white]="isPdfAvailable(inv) && !openingPdf()[inv.invoice_id]"
                      [class.bg-gray-100]="openingPdf()[inv.invoice_id] || !isPdfAvailable(inv)"
                      [class.text-gray-300]="!isPdfAvailable(inv)"
                      [class.cursor-not-allowed]="!isPdfAvailable(inv)"
                      [class.cursor-wait]="openingPdf()[inv.invoice_id]"
                      [attr.title]="!isPdfAvailable(inv) ? ('portal.invoices.pdfNotYetAvailable' | translate) : ('finance.view_pdf' | translate)"
                    >
                      @if (openingPdf()[inv.invoice_id]) {
                        <div class="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      } @else {
                        <span class="material-symbols-outlined text-lg">picture_as_pdf</span>
                      }
                    </button>
                  </div>
                </div>
              </div>

              <!-- ACCORDION BODY (MASTER-DETAIL DETAILS) -->
              @if (accordionItem.expanded) {
                <div class="border-t border-gray-100 bg-gray-50/50 p-4 md:p-6 animate-fade-in">
                  @if (loadingDetails()[inv.invoice_id]) {
                    <div class="flex items-center justify-center p-8 gap-3 text-gray-500">
                      <div class="w-5 h-5 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                      <span class="text-xs font-semibold">{{ 'portal.invoices.loadingDetails' | translate }}</span>
                    </div>
                  } @else {
                    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                      <!-- LEFT PANE: INVOICE POSITIONS (COL 1-7) -->
                      <div class="lg:col-span-7 bg-white rounded-xl border border-gray-200/80 shadow-sm p-4 md:p-5">
                        <div class="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                          <span class="material-symbols-outlined text-blue-600 text-lg">inventory_2</span>
                          <h3 class="text-xs font-black uppercase tracking-wider text-gray-700 m-0">
                            {{ 'finance.positions' | translate }}
                          </h3>
                          <span class="ml-auto text-xs text-gray-400 font-bold">
                            {{ (detailsCache()[inv.invoice_id]?.positions || []).length }} {{ 'portal.invoices.items' | translate }}
                          </span>
                        </div>

                        <div class="overflow-x-auto">
                          <table class="w-full text-left text-xs">
                            <thead>
                              <tr class="text-[10px] uppercase font-bold text-gray-400 border-b border-gray-100 pb-2">
                                <th class="py-2 pr-1 w-6">#</th>
                                <th class="py-2 pr-2">{{ 'finance.invoice.service_item' | translate }}</th>
                                <th class="py-2 px-1 text-center w-12">{{ 'finance.quantity' | translate }}</th>
                                <th class="py-2 px-2 text-right whitespace-nowrap min-w-[110px]">{{ 'finance.amount_net' | translate }}</th>
                                <th class="py-2 pl-2 text-right whitespace-nowrap min-w-[110px]">{{ 'finance.amount_gross' | translate }}</th>
                              </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-50">
                              @for (pos of detailsCache()[inv.invoice_id]?.positions || []; track pos.invoice_position_id) {
                                <tr class="hover:bg-gray-50/50">
                                  <td class="py-2.5 pr-1 font-bold text-gray-400">{{ pos.position || 1 }}</td>
                                  <td class="py-2.5 pr-2 font-semibold text-gray-800">
                                    {{ pos.description || '-' }}
                                    @if (pos.price_code_short) {
                                      <span class="ml-1 text-[10px] text-gray-400 font-normal">({{ pos.price_code_short }})</span>
                                    }
                                  </td>
                                  <td class="py-2.5 px-1 text-center text-gray-600 tabular-nums">{{ pos.quantity }}</td>
                                  <td class="py-2.5 px-2 text-right text-gray-600 tabular-nums whitespace-nowrap">
                                    {{ formatCurrency(pos.amount_net) }}
                                  </td>
                                  <td class="py-2.5 pl-2 text-right font-bold text-gray-900 tabular-nums whitespace-nowrap">
                                    {{ formatCurrency(pos.amount_gross || pos.amount_net) }}
                                  </td>
                                </tr>
                              } @empty {
                                <tr>
                                  <td colspan="5" class="py-4 text-center text-gray-400 italic">
                                    {{ 'generic.no_results' | translate }}
                                  </td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- RIGHT PANE: INSTALLMENT SCHEDULE & STATUSES (COL 8-12) -->
                      <div class="lg:col-span-5 bg-white rounded-xl border border-gray-200/80 shadow-sm p-4 md:p-5">
                        <div class="flex items-center gap-2 mb-4 pb-2 border-b border-gray-100">
                          <span class="material-symbols-outlined text-emerald-600 text-lg">payments</span>
                          <h3 class="text-xs font-black uppercase tracking-wider text-gray-700 m-0">
                            {{ 'finance.installments' | translate }}
                          </h3>
                          <span class="ml-auto text-xs text-gray-400 font-bold">
                            {{ (detailsCache()[inv.invoice_id]?.installments || []).length }} {{ 'portal.invoices.rates' | translate }}
                          </span>
                        </div>

                        <div class="flex flex-col gap-2.5">
                          @for (inst of detailsCache()[inv.invoice_id]?.installments || []; track inst.invoice_installment_id) {
                            <div class="p-3 rounded-xl border border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3 transition-colors hover:bg-gray-50">
                              <!-- Installment ID / Due date -->
                              <div>
                                <div class="flex items-center gap-2 mb-0.5">
                                  <span class="text-xs font-black text-gray-800">
                                    {{ 'finance.installment' | translate }} #{{ inst.installment_no }}
                                  </span>
                                  @if (inst.reminder_level && inst.reminder_level > 0) {
                                    <span class="px-1.5 py-0.2 text-[9px] font-black rounded uppercase bg-red-100 text-red-700">
                                      Mahnstufe {{ inst.reminder_level }}
                                    </span>
                                  }
                                </div>
                                <div class="text-[11px] text-gray-500 flex items-center gap-1">
                                  <span class="material-symbols-outlined text-xs text-gray-400">calendar_today</span>
                                  <span>{{ inst.due_date | date:'dd.MM.yyyy' }}</span>
                                </div>
                              </div>

                              <!-- Amount and Color-coded Status -->
                              <div class="text-right flex flex-col items-end gap-1">
                                <div class="text-xs font-bold text-gray-900 tabular-nums">
                                  {{ formatCurrency(inst.amount) }}
                                </div>

                                <!-- STATUS COLORS: Paid=green, Overdue=red, To pay=yellow, Paused/Aborted=neutral -->
                                @if (getInstallmentStatus(inst, inv) === 'paid') {
                                  <span class="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center gap-1">
                                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                                    {{ 'finance.debtors.paid' | translate }}
                                  </span>
                                } @else if (getInstallmentStatus(inst, inv) === 'paused') {
                                  <span class="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-purple-100 text-purple-800 border border-purple-200 flex items-center gap-1">
                                    <span class="w-1.5 h-1.5 rounded-full bg-purple-600"></span>
                                    {{ 'finance.status.paused' | translate }}
                                  </span>
                                } @else if (getInstallmentStatus(inst, inv) === 'aborted') {
                                  <span class="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-gray-100 text-gray-700 border border-gray-200 flex items-center gap-1">
                                    <span class="w-1.5 h-1.5 rounded-full bg-gray-500"></span>
                                    {{ 'finance.status.aborted' | translate }}
                                  </span>
                                } @else if (getInstallmentStatus(inst, inv) === 'overdue') {
                                  <span class="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-red-100 text-red-800 border border-red-200 flex items-center gap-1">
                                    <span class="w-1.5 h-1.5 rounded-full bg-red-600"></span>
                                    {{ 'finance.debtors.overdue' | translate }}
                                  </span>
                                } @else {
                                  <span class="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1">
                                    <span class="w-1.5 h-1.5 rounded-full bg-amber-600"></span>
                                    {{ 'finance.debtors.open' | translate }}
                                  </span>
                                }
                              </div>
                            </div>
                          } @empty {
                            <div class="py-4 text-center text-gray-400 italic text-xs">
                              {{ 'generic.no_results' | translate }}
                            </div>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </div>
              }
            </cdk-accordion-item>
          }
        </cdk-accordion>
      }

      <!-- CONFIRMATION MODAL FOR SWITCHING INSTALLMENTS -->
      @if (confirmModalState().isOpen && confirmModalState().school; as school) {
        <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-fade-in">
          <div class="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 border border-gray-100 flex flex-col gap-4">
            <div class="flex items-start gap-4">
              <div class="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
                   [ngClass]="confirmModalState().targetEnable ? 'bg-indigo-50 text-indigo-600' : 'bg-amber-50 text-amber-600'">
                <span class="material-symbols-outlined text-2xl">
                  {{ confirmModalState().targetEnable ? 'credit_card' : 'receipt_long' }}
                </span>
              </div>
              <div class="flex-1 pr-4">
                <h3 class="text-base font-black text-gray-900 m-0">
                  {{ confirmModalState().targetEnable
                    ? ('portal.invoices.modal.titleToSix' | translate)
                    : ('portal.invoices.modal.titleToOne' | translate) }}
                </h3>
                <p class="text-xs text-gray-500 mt-1 mb-0">
                  {{ school.school_name }}
                </p>
              </div>
              <button
                type="button"
                (click)="closeConfirmModal()"
                class="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-all cursor-pointer flex-shrink-0"
              >
                <span class="material-symbols-outlined text-xl">close</span>
              </button>
            </div>

            <!-- Warning Notice -->
            <div class="bg-amber-50/80 border border-amber-200/80 rounded-xl p-3.5 text-xs text-amber-900 flex flex-col gap-2">
              <div class="flex items-center gap-2 font-bold text-amber-800">
                <span class="material-symbols-outlined text-base">warning</span>
                <span>{{ 'portal.invoices.modal.importantNote' | translate }}</span>
              </div>
              <p class="m-0 leading-relaxed">
                {{ 'portal.invoices.modal.plannedOnlyDesc' | translate }}
              </p>
              <ul class="m-0 pl-4 list-disc space-y-1 text-[11px] text-amber-800/90">
                <li>{{ 'portal.invoices.modal.rule1' | translate }}</li>
                <li>{{ 'portal.invoices.modal.rule2' | translate }}</li>
                <li>{{ 'portal.invoices.modal.rule3' | translate }}</li>
              </ul>
            </div>

            <!-- Details Summary -->
            <div class="bg-gray-50 rounded-xl p-3 border border-gray-100 text-xs flex flex-col gap-1.5">
              <div class="flex justify-between items-center text-gray-600">
                <span>{{ 'portal.invoices.modal.newModeLabel' | translate }}:</span>
                <span class="font-bold text-gray-900">
                  {{ confirmModalState().targetEnable
                    ? ('portal.invoices.installments.sixInstallments' | translate)
                    : ('portal.invoices.installments.oneInstallment' | translate) }}
                </span>
              </div>
              <div class="flex justify-between items-center text-gray-600">
                <span>{{ 'portal.invoices.modal.surchargeRuleLabel' | translate }}:</span>
                <span class="font-semibold text-gray-800">{{ getSurchargeDescription(school) }}</span>
              </div>
              @if (school.planned_invoices_count > 0) {
                <div class="flex justify-between items-center text-amber-700 font-semibold pt-1 border-t border-gray-200/60">
                  <span>{{ 'portal.invoices.modal.affectedCountLabel' | translate }}:</span>
                  <span>{{ school.planned_invoices_count }} {{ 'portal.invoices.invoicesUnit' | translate }}</span>
                </div>
              }
            </div>

            <!-- Actions -->
            <div class="flex items-center justify-end gap-2.5 pt-2 border-t border-gray-100">
              <button
                type="button"
                (click)="closeConfirmModal()"
                class="px-4 py-2 rounded-xl text-xs font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all cursor-pointer"
              >
                {{ 'generic.cancel' | translate }}
              </button>
              <button
                type="button"
                (click)="executeInstallmentSwitch()"
                class="px-4 py-2 rounded-xl text-xs font-bold text-white transition-all shadow-sm flex items-center gap-1.5 cursor-pointer"
                [ngClass]="confirmModalState().targetEnable
                  ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-100'
                  : 'bg-amber-600 hover:bg-amber-700 shadow-amber-100'"
              >
                <span class="material-symbols-outlined text-sm">check</span>
                <span>{{ 'portal.invoices.modal.confirmBtn' | translate }}</span>
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RechnungenComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly contactService = inject(ContactService);
  private readonly invoiceService = inject(StudentInvoiceService);
  private readonly i18n = inject(I18nStore);

  // Status Enum
  protected readonly InvoiceStatusId = InvoiceStatusId;

  // State Signals
  readonly loading = signal<boolean>(false);
  readonly error = signal<string | null>(null);
  readonly contactId = signal<number | null>(null);
  readonly invoices = signal<StudentInvoice[]>([]);
  readonly hidePaidInvoices = signal<boolean>(false);
  readonly hidePlannedInvoices = signal<boolean>(false);

  // Cache for loaded details per invoice
  readonly detailsCache = signal<Record<number, StudentInvoiceDetailResponse>>({});
  readonly loadingDetails = signal<Record<number, boolean>>({});
  readonly openingPdf = signal<Record<number, boolean>>({});

  // Installment Management State
  readonly schoolInstallmentStatuses = signal<StudentInstallmentStatus[]>([]);
  readonly loadingInstallmentStatus = signal<boolean>(false);
  readonly recalculatingSchoolId = signal<number | null>(null);

  // Confirmation Modal State
  readonly confirmModalState = signal<{
    isOpen: boolean;
    school: StudentInstallmentStatus | null;
    targetEnable: boolean;
  }>({
    isOpen: false,
    school: null,
    targetEnable: false,
  });

  // Computed Values for Summary Cards
  // Summary card 2 "Planned invoices" = status 650 only. Paused (654) and
  // Aborted (655) invoices are never 650, so they never enter this card.
  readonly plannedInvoices = computed(() => {
    return this.invoices().filter((inv) => inv.fk_status_id === InvoiceStatusId.Planned);
  });

  // Summary card 1 "Active invoices / open amount" = everything that is not
  // Planned, minus Paused (654) and Aborted (655): those are not payable right
  // now (Unterbruch/Abbruch), so their open amounts must not count toward what
  // the student is asked to pay. They still show up in the invoice list below.
  readonly notPlannedInvoices = computed(() => {
    return this.invoices().filter(
      (inv) => inv.fk_status_id !== InvoiceStatusId.Planned && !this.isInvoiceInactive(inv)
    );
  });

  readonly plannedCount = computed(() => this.plannedInvoices().length);
  readonly notPlannedCount = computed(() => this.notPlannedInvoices().length);

  readonly plannedOpenAmount = computed(() => {
    return this.plannedInvoices().reduce((sum, inv) => sum + (inv.open_amount || inv.amount_gross || 0), 0);
  });

  readonly notPlannedOpenAmount = computed(() => {
    return this.notPlannedInvoices().reduce((sum, inv) => sum + (inv.open_amount || 0), 0);
  });

  readonly paidInvoicesCount = computed(() => {
    return this.invoices().filter((inv) => inv.open_amount <= 0 || inv.fk_status_id === 652).length;
  });

  // Displayed invoices according to filters
  readonly displayedInvoices = computed(() => {
    let list = this.invoices();

    if (this.hidePlannedInvoices()) {
      list = list.filter((inv) => inv.fk_status_id !== InvoiceStatusId.Planned);
    }

    if (this.hidePaidInvoices()) {
      list = list.filter((inv) => inv.open_amount > 0 && inv.fk_status_id !== InvoiceStatusId.Sent && inv.fk_status_id !== 652);
    }

    return list;
  });

  ngOnInit(): void {
    this.loadInvoicesAndData();
  }

  /**
   * Load student contact and invoices
   */
  public loadInvoicesAndData(): void {
    this.loading.set(true);
    this.error.set(null);

    const userInfo = this.authService.getUserInfo();
    const userEmail = userInfo?.sub || userInfo?.email;

    if (!userEmail) {
      this.error.set(this.i18n.t('portal.documents.error.noEmail'));
      this.loading.set(false);
      return;
    }

    this.contactService
      .getContactByEmail(userEmail)
      .pipe(
        switchMap((contact) => {
          this.contactId.set(contact.contact_id);
          return forkJoin({
            invoices: this.invoiceService.getInvoices(contact.contact_id).pipe(
              catchError((err) => {
                console.error('❌ Error loading invoices:', err);
                return of([] as StudentInvoice[]);
              })
            ),
            installmentStatuses: this.invoiceService.getStudentInstallmentStatus(contact.contact_id).pipe(
              catchError((err) => {
                console.error('❌ Error loading student installment statuses:', err);
                return of([] as StudentInstallmentStatus[]);
              })
            ),
          });
        }),
        catchError((err) => {
          console.error('❌ Error in initial load:', err);
          this.error.set(this.i18n.t('portal.invoices.error.load'));
          this.loading.set(false);
          return of(null);
        })
      )
      .subscribe((res) => {
        if (!res) return;
        this.invoices.set(res.invoices);
        this.schoolInstallmentStatuses.set(res.installmentStatuses);
        this.loading.set(false);
      });
  }

  /**
   * Toggle hide/show paid invoices
   */
  toggleHidePaid(): void {
    this.hidePaidInvoices.update((v) => !v);
  }

  /**
   * Toggle hide/show planned invoices
   */
  toggleHidePlanned(): void {
    this.hidePlannedInvoices.update((v) => !v);
  }

  /**
   * Toggle accordion expansion and lazy load invoice details
   */
  onToggleAccordion(accordionItem: any, inv: StudentInvoice): void {
    accordionItem.toggle();
    if (accordionItem.expanded && !this.detailsCache()[inv.invoice_id]) {
      this.loadDetails(inv.invoice_id);
    }
  }

  /**
   * Fetch invoice details with positions and installments
   */
  private loadDetails(invoiceId: number): void {
    this.loadingDetails.update((map) => ({ ...map, [invoiceId]: true }));

    this.invoiceService.getInvoiceDetails(invoiceId).subscribe({
      next: (details) => {
        this.detailsCache.update((map) => ({ ...map, [invoiceId]: details }));
        this.loadingDetails.update((map) => ({ ...map, [invoiceId]: false }));
      },
      error: (err) => {
        console.error(`❌ Error loading details for invoice ${invoiceId}:`, err);
        this.loadingDetails.update((map) => ({ ...map, [invoiceId]: false }));
      },
    });
  }

  /**
   * Whether the invoice is currently inactive: Paused (654, Unterbruch —
   * payment obligation suspended) or Aborted (655, Abbruch). Such invoices are
   * not payable, so they are excluded from the active/open-to-pay summary
   * totals and from PDF availability, and their installments do not render as
   * overdue/open. They remain visible in the invoice list with their own badge.
   */
  isInvoiceInactive(inv: StudentInvoice): boolean {
    return (
      inv.fk_status_id === InvoiceStatusId.Paused || inv.fk_status_id === InvoiceStatusId.Aborted
    );
  }

  /**
   * Whether the invoice PDF is available for download/viewing.
   * Planned (650) and ToSend (651) invoices have not yet been generated/sent as PDFs.
   * Paused (654) and Aborted (655) invoices were never dispatched either —
   * their PDFs do not exist and a request would 404 on the backend.
   */
  isPdfAvailable(inv: StudentInvoice): boolean {
    if (!inv || !inv.fk_status_id) {
      return false;
    }
    return (
      inv.fk_status_id !== InvoiceStatusId.Planned &&
      inv.fk_status_id !== InvoiceStatusId.ToSend &&
      !this.isInvoiceInactive(inv)
    );
  }

  /**
   * Secure PDF opening
   * Fetches the PDF data as a Blob directly through the backend API,
   * then opens a local in-memory object URL (blob:...).
   * This completely masks the Azure Storage URL and SAS token from the browser address bar.
   */
  fetchAndOpenPdf(inv: StudentInvoice): void {
    if (!this.isPdfAvailable(inv) || this.openingPdf()[inv.invoice_id]) {
      return;
    }

    this.openingPdf.update((map) => ({ ...map, [inv.invoice_id]: true }));

    this.invoiceService.downloadInvoicePdfBlob(inv.invoice_id).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'application/pdf' });
        const objectUrl = window.URL.createObjectURL(file);
        
        // Translated title: e.g. "Rechnung 104657.pdf"
        const invoiceLabel = this.i18n.t('finance.invoice') || 'Rechnung';
        const docTitle = `${invoiceLabel} ${inv.invoice_id}.pdf`;

        // Open viewer window with customized title
        const win = window.open('', '_blank');
        if (win) {
          win.document.title = docTitle;
          win.document.body.style.margin = '0';
          win.document.body.style.height = '100vh';
          win.document.body.style.overflow = 'hidden';
          win.document.body.style.backgroundColor = '#525659';

          const iframe = win.document.createElement('iframe');
          iframe.src = objectUrl;
          iframe.title = docTitle;
          iframe.style.width = '100%';
          iframe.style.height = '100%';
          iframe.style.border = 'none';
          win.document.body.appendChild(iframe);
        } else {
          // Fallback if popup blocker intercepted empty window
          window.open(objectUrl, '_blank', 'noopener,noreferrer');
        }

        this.openingPdf.update((map) => ({ ...map, [inv.invoice_id]: false }));
      },
      error: (err) => {
        console.error('❌ Could not load or open invoice PDF:', err);
        this.openingPdf.update((map) => ({ ...map, [inv.invoice_id]: false }));
      },
    });
  }

  /**
   * Determine installment display status: paid | paused | aborted | overdue | to_pay.
   * A fully paid installment always wins ("Bezahlt"). Installments of an
   * inactive invoice (654 Pausiert / 655 Abgebrochen) render as paused/aborted
   * instead of overdue/open, so a suspended or broken contract never shows
   * misleading dunning states.
   */
  getInstallmentStatus(
    inst: StudentInvoiceInstallment,
    inv: StudentInvoice
  ): 'paid' | 'paused' | 'aborted' | 'overdue' | 'to_pay' {
    if (inst.open_amount <= 0 || inst.fk_status_id === 652) {
      return 'paid';
    }

    if (inv.fk_status_id === InvoiceStatusId.Paused) {
      return 'paused';
    }

    if (inv.fk_status_id === InvoiceStatusId.Aborted) {
      return 'aborted';
    }

    if (inst.due_date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(inst.due_date);
      due.setHours(0, 0, 0, 0);

      if (due < today) {
        return 'overdue';
      }
    }

    return 'to_pay';
  }

  /**
   * Status badge styling for invoice
   */
  getStatusBadgeClass(inv: StudentInvoice): string {
    if (inv.open_amount <= 0 || inv.fk_status_id === 652) {
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    }
    if (inv.fk_status_id === InvoiceStatusId.Planned) {
      return 'bg-amber-50 text-amber-700 border-amber-200';
    }
    if (inv.fk_status_id === InvoiceStatusId.Cancelled) {
      return 'bg-gray-100 text-gray-500 border-gray-200';
    }
    if (inv.fk_status_id === InvoiceStatusId.Paused) {
      return 'bg-purple-50 text-purple-700 border-purple-200';
    }
    if (inv.fk_status_id === InvoiceStatusId.Aborted) {
      return 'bg-gray-100 text-gray-500 border-gray-200';
    }
    return 'bg-blue-50 text-blue-700 border-blue-200';
  }

  /**
   * Status label
   */
  getStatusLabel(inv: StudentInvoice): string {
    if (inv.open_amount <= 0 || inv.fk_status_id === 652) {
      return this.i18n.t('finance.debtors.paid');
    }
    if (inv.fk_status_id === InvoiceStatusId.Planned) {
      return this.i18n.t('finance.status.planned');
    }
    if (inv.fk_status_id === InvoiceStatusId.Cancelled) {
      return this.i18n.t('finance.status.cancelled');
    }
    if (inv.fk_status_id === InvoiceStatusId.Paused) {
      return this.i18n.t('finance.status.paused');
    }
    if (inv.fk_status_id === InvoiceStatusId.Aborted) {
      return this.i18n.t('finance.status.aborted');
    }
    return inv.status || this.i18n.t('finance.debtors.open');
  }

  /**
   * Format amount in Swiss notation with space: "CHF 1'234.50"
   */
  formatCurrency(val: number | null | undefined): string {
    if (val === null || val === undefined) {
      return '-';
    }
    const formattedNumber = new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);
    return `CHF ${formattedNumber}`;
  }

  /**
   * Open the confirmation dialog for switching installment mode
   */
  openConfirmInstallmentModal(school: StudentInstallmentStatus): void {
    const targetEnable = !school.has_installments_enabled;
    this.confirmModalState.set({
      isOpen: true,
      school,
      targetEnable,
    });
  }

  /**
   * Close confirmation dialog
   */
  closeConfirmModal(): void {
    this.confirmModalState.set({
      isOpen: false,
      school: null,
      targetEnable: false,
    });
  }

  /**
   * Execute recalculation of installments (1 -> 6 or 6 -> 1)
   */
  executeInstallmentSwitch(): void {
    const modal = this.confirmModalState();
    const contactId = this.contactId();
    if (!modal.school || !contactId) {
      this.closeConfirmModal();
      return;
    }

    const schoolId = modal.school.school_id;
    const targetEnable = modal.targetEnable;

    this.recalculatingSchoolId.set(schoolId);
    this.closeConfirmModal();

    this.invoiceService
      .recalculateInstallments(contactId, targetEnable, schoolId)
      .subscribe({
        next: (res) => {
          // Clear details cache to refresh expanded accordions
          this.detailsCache.set({});
          // Reload invoices and updated school installment status
          this.loadInvoicesAndData();
          this.recalculatingSchoolId.set(null);
        },
        error: (err) => {
          console.error('❌ Error switching installment mode:', err);
          this.recalculatingSchoolId.set(null);
          this.error.set(this.i18n.t('portal.invoices.error.recalculate') || 'Fehler beim Ändern des Ratenzahlungsmodus.');
        },
      });
  }

  /**
   * Get formatted surcharge description from app.schools configuration
   */
  getSurchargeDescription(school: StudentInstallmentStatus): string {
    if (!school.installments_type || school.installments_value === null || school.installments_value === undefined) {
      return this.i18n.t('portal.invoices.installments.noSurcharge') || 'Kein Aufpreis';
    }

    const type = String(school.installments_type).trim();
    if (type === '%') {
      const pctLabel = this.i18n.t('portal.invoices.installments.percentageSurcharge') || 'Aufpreis';
      return `${pctLabel}: ${school.installments_value}%`;
    }

    if (type === 'Amount' || type === 'Betrag') {
      const feeLabel = this.i18n.t('portal.invoices.installments.fixedSurcharge') || 'Aufpreis pro Rechnung';
      return `${feeLabel}: ${this.formatCurrency(school.installments_value)}`;
    }

    return `${school.installments_type}: ${school.installments_value}`;
  }
}

